// src/components/Town/budgetCard.js
import React from "react";

/**
 * BudgetCard wraps tab navigation and provides layout for embedded content like
 * reports or data views. Use this to keep layout/style consistent across budget sections.
 *
 * @param {string} activeTab - Currently active tab (optional if no tabs)
 * @param {Function} setActiveTab - Updates active tab state (required only if using tabs)
 * @param {ReactNode} children - Tab-specific content to render below the tabs
 * @param {string[]} tabs - Tab labels (e.g. ["Tables", "Data"])
 * @param {string} warningMessage - Optional warning to display
 * @param {boolean} loading - Whether to show loading text
 * @param {boolean} wrapChildren - Whether to wrap children in the padded body div (default true)
 */
function BudgetCard({
  activeTab,
  setActiveTab,
  children,
  tabs = ["Tables", "Data"],
  warningMessage = "",
  loading = false,
  wrapChildren = true,
}) {
  // Only show tabs if we have:
  //  - a non-empty tabs array
  //  - a real setActiveTab function
  //  - a string activeTab value
  const hasTabs =
    Array.isArray(tabs) &&
    tabs.length > 0 &&
    typeof setActiveTab === "function" &&
    typeof activeTab === "string";

  return (
    <div className="card bg-base-100 shadow-xl rounded-xl mb-6 w-full">
      {hasTabs && (
        <div className="tabs tabs-bordered px-6 pt-6">
          {tabs.map((tab) => (
            <a
              key={tab}
              className={`tab text-lg ${
                activeTab === tab
                  ? "tab-active border-b-2 border-primary font-semibold text-gray-900"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </a>
          ))}
        </div>
      )}

      {warningMessage && (
        <div className="px-6 text-red-500 mt-3 font-medium text-sm">
          {warningMessage}
        </div>
      )}

      {loading ? (
        <div className="px-1 py-2 sm:px-6 sm:py-4 text-center text-sm text-gray-400">
          Loading...
        </div>
      ) : wrapChildren ? (
        <div className="px-1 py-2 sm:px-6 sm:py-4">{children}</div>
      ) : (
        <>{children}</>
      )}
    </div>
  );
}

export default BudgetCard;
